export type TradingViewCandlestickLabelDirection = 'up' | 'down'

export type TradingViewCandlestickPatternLabelOptions = {
    horizontalPixelRatio?: number
    verticalPixelRatio?: number
    markerHeight?: number
    pointerHeight?: number
    minMarkerWidth?: number
}

type CandlestickLabelMetrics = {
    width: number
    height: number
    pointerH: number
    fontSize: number
}

const measureCandlestickLabel = (
    text: string,
    options: TradingViewCandlestickPatternLabelOptions,
    measurer?: Pick<CanvasRenderingContext2D, 'font' | 'measureText'>,
): CandlestickLabelMetrics => {
    const hpr = options.horizontalPixelRatio ?? 1
    const vpr = options.verticalPixelRatio ?? 1
    const height = (options.markerHeight ?? 14) * vpr
    const pointerH = (options.pointerHeight ?? 6) * vpr
    const minWidth = (options.minMarkerWidth ?? 22) * hpr
    const fontSize = (text.length > 1 ? 8 : 10) * vpr
    const padX = 6 * hpr

    let textWidth = text.length * fontSize * 0.65
    if (measurer) {
        measurer.font = `bold ${fontSize}px -apple-system, BlinkMacSystemFont, "Trebuchet MS", Roboto, sans-serif`
        textWidth = measurer.measureText(text).width
    }

    return {
        width: Math.max(minWidth, textWidth + padX * 2),
        height,
        pointerH,
        fontSize,
    }
}

/** TradingView built-in candlestick pattern badge — compact tag with pointer toward the candle */
export function drawTradingViewCandlestickPatternLabel(
    ctx: CanvasRenderingContext2D,
    centerX: number,
    baseY: number,
    text: string,
    backgroundColor: string,
    direction: TradingViewCandlestickLabelDirection,
    options: TradingViewCandlestickPatternLabelOptions = {},
) {
    const { width, height, pointerH, fontSize } = measureCandlestickLabel(text, options, ctx)
    const left = centerX - width / 2
    const right = centerX + width / 2

    ctx.save()
    ctx.fillStyle = backgroundColor
    ctx.beginPath()

    if (direction === 'up') {
        const rectTop = baseY - height
        const tipY = rectTop - pointerH
        ctx.moveTo(centerX, tipY)
        ctx.lineTo(right, rectTop)
        ctx.lineTo(right, baseY)
        ctx.lineTo(left, baseY)
        ctx.lineTo(left, rectTop)
        ctx.closePath()
        ctx.fill()

        ctx.fillStyle = '#FFFFFF'
        ctx.font = `bold ${fontSize}px -apple-system, BlinkMacSystemFont, "Trebuchet MS", Roboto, sans-serif`
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillText(text, centerX, rectTop + height / 2)
    } else {
        const rectTop = baseY - height
        const tipY = baseY + pointerH
        ctx.moveTo(centerX, tipY)
        ctx.lineTo(right, baseY)
        ctx.lineTo(right, rectTop)
        ctx.lineTo(left, rectTop)
        ctx.lineTo(left, baseY)
        ctx.closePath()
        ctx.fill()

        ctx.fillStyle = '#FFFFFF'
        ctx.font = `bold ${fontSize}px -apple-system, BlinkMacSystemFont, "Trebuchet MS", Roboto, sans-serif`
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillText(text, centerX, rectTop + height / 2)
    }

    ctx.restore()
}

export function measureTradingViewCandlestickPatternLabelBounds(
    centerX: number,
    baseY: number,
    text: string,
    direction: TradingViewCandlestickLabelDirection,
    options: TradingViewCandlestickPatternLabelOptions = {},
    measurer?: Pick<CanvasRenderingContext2D, 'font' | 'measureText'>,
): { left: number; right: number; top: number; bottom: number } {
    const { width, height, pointerH } = measureCandlestickLabel(text, options, measurer)
    const left = centerX - width / 2
    const right = centerX + width / 2

    if (direction === 'up') {
        const rectTop = baseY - height
        const tipY = rectTop - pointerH
        return { left, right, top: tipY, bottom: baseY }
    }

    const rectTop = baseY - height
    const tipY = baseY + pointerH
    return { left, right, top: rectTop, bottom: tipY }
}
